import React, { useState, useRef, useEffect, useCallback } from 'react';
import { styled } from '@mui/material';
import { CssOutlinedInput, CssFormHelperText } from '@components/Input';

const OtpContainer = styled('div')({
    display: 'flex',
    gap: '8px',
    position: 'relative',
    paddingBottom: '16px',
});

const OtpInput = styled(CssOutlinedInput)({
    width: '40px',
    height: '40px',
    '& input': {
        textAlign: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1F4C5C',
        borderWidth: '2px',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#B00020',
        borderWidth: '2px',
    },
});

const OtpCode = React.forwardRef(
    (
        { length = 4, value = '', onChange, onBlur, error = false, helperText, disabled = false, autoFocus = false },
        ref,
    ) => {
        const [otpValues, setOtpValues] = useState(
            Array(length)
                .fill('')
                .map((_, index) => value[index] || ''),
        );
        const inputRefs = useRef([]);

        useEffect(() => {
            //INFO: Update the OTP values when the value changes
            const newValues = Array(length)
                .fill('')
                .map((_, index) => value[index] || '');
            setOtpValues(newValues);
        }, [value, length]);

        useEffect(() => {
            //INFO: Auto-focus the first input if enabled
            if (autoFocus && inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
        }, [autoFocus]);

        const handleInputChange = useCallback(
            (index, inputValue) => {
                //INFO: Only allow single digit
                const digit = inputValue.replace(/\D/g, '').slice(0, 1);

                const newOtpValues = [...otpValues];
                newOtpValues[index] = digit;
                setOtpValues(newOtpValues);

                //INFO: Call onChange with the complete OTP string
                if (newOtpValues.every(v => v !== '')) {
                    const otpString = newOtpValues.join('');
                    onChange?.(otpString);
                }

                //INFO: Auto-focus next input
                if (digit && index < length - 1) {
                    inputRefs.current[index + 1]?.focus();
                }
            },
            [otpValues, length, onChange],
        );

        const handleKeyDown = useCallback(
            (index, event) => {
                const { key } = event;

                if (key === 'Backspace') {
                    if (!otpValues[index] && index > 0) {
                        //INFO: If current input is empty, move to previous input
                        inputRefs.current[index - 1]?.focus();
                    } else {
                        //INFO: Clear current input
                        handleInputChange(index, '');
                    }
                } else if (key === 'ArrowLeft' && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                } else if (key === 'ArrowRight' && index < length - 1) {
                    inputRefs.current[index + 1]?.focus();
                } else if (key === 'Delete') {
                    handleInputChange(index, '');
                }
            },
            [otpValues, length, handleInputChange],
        );

        const handlePaste = useCallback(
            event => {
                event.preventDefault();
                const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

                if (pastedData.length > 0) {
                    const newOtpValues = Array(length).fill('');
                    for (let i = 0; i < pastedData.length; i++) {
                        newOtpValues[i] = pastedData[i];
                    }
                    setOtpValues(newOtpValues);

                    const otpString = newOtpValues.join('');
                    onChange?.(otpString);

                    //INFO: Focus the next empty input or the last input
                    const nextEmptyIndex = newOtpValues.findIndex(val => val === '');
                    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : length - 1;
                    inputRefs.current[focusIndex]?.focus();
                }
            },
            [length, onChange],
        );

        const anyInputFocused = useCallback(() => {
            const active = document.activeElement;
            return inputRefs.current.some(el => el === active);
        }, []);

        const handleFocus = useCallback(index => {
            inputRefs.current[index]?.select();
        }, []);

        const handleBlur = useCallback(() => {
            setTimeout(() => {
                if (!anyInputFocused()) {
                    const otpString = otpValues.join('');
                    onChange?.(otpString);
                    onBlur?.();
                }
            }, 0);
        }, [otpValues, onChange, onBlur, anyInputFocused]);

        return (
            <OtpContainer ref={ref}>
                {otpValues.map((digit, index) => (
                    <OtpInput
                        key={index}
                        inputRef={el => {
                            if (el) {
                                inputRefs.current[index] = el;
                            }
                        }}
                        value={digit}
                        onChange={e => handleInputChange(index, e.target.value)}
                        onKeyDown={e => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        onFocus={() => handleFocus(index)}
                        onBlur={handleBlur}
                        error={error}
                        disabled={disabled}
                    />
                ))}
                {helperText && <CssFormHelperText error={error}>{helperText}</CssFormHelperText>}
            </OtpContainer>
        );
    },
);

OtpCode.displayName = 'OtpCode';

export default OtpCode;
